import { ALLOWED_TAGS, sanitizeHtml } from '@allibee/components/common/markdown/marked';
import type { MarkdownConfig } from '@allibee/components/common/markdown/types';

/**
 * BA-3079: 해외 DB(EU 법령 등) 본문 텍스트 fallback 전용 마크다운 설정.
 *
 * 백엔드 RAG 응답이
 *   1) backtick(`§ 19(1)` 같은 인용 표기)과 raw HTML 태그(<section>, <article> 등)를 마크다운에 섞어 보내거나
 *   2) EU 법령처럼 통째로 XHTML 문서(<html>/<head>/<body>/<col> 포함)로 내려보낸다.
 *
 * 기본 설정으로는 (1) 코드 토큰이 escape 된 코드 박스로, (2) ALLOWED_TAGS 밖 태그가 모두 escape 되어
 * raw 마크업이 텍스트로 노출되는 문제가 있다.
 *
 * 이 설정은
 *   - 코드 토큰을 본문 흐름에 그대로 흘리되 sanitize 를 거쳐 위험 태그(<script> 등)는 escape 하고
 *   - 해외 법령 시멘틱 태그·테이블 컬럼 정의를 화이트리스트에 추가하며
 *   - EU 법령처럼 문서 래퍼(<html>/<head>...</head>/<body>)와 메타(<meta>/<link>/<title>)를 통째로 제거해
 *     사용자가 보는 본문에 잡음이 노출되지 않도록 하고
 *   - sanitize 통과한 태그에 프로젝트 마크다운 렌더러와 동일한 Tailwind 클래스를 입혀
 *     외국 법령 측 클래스(예: oj-normal)가 잔재로 남는 대신 일반 마크다운 본문과 시각이 일치하도록 한다.
 *
 * 입력은 백엔드 시스템 큐레이션 응답으로 한정되며, UGC 본문에는 사용하지 않는다.
 */

/**
 * 해외 법령 본문이 자주 쓰는 시멘틱 블록·테이블 컬럼 정의 태그.
 * sanitize 화이트리스트에 추가한다.
 */
const FOREIGN_LEGISLATION_HTML_TAGS = [
    // 시멘틱 블록
    'section',
    'article',
    'header',
    'footer',
    'aside',
    'nav',
    'dl',
    'dt',
    'dd',
    'figure',
    'figcaption',
    // 테이블 레이아웃 (EU 법령은 column width 정의를 위해 <col>/<colgroup> 사용)
    'col',
    'colgroup',
    'caption',
] as const;

/**
 * code/codespan/html 본문에서 sanitize 를 거쳐 raw HTML 토큰을 안전하게 통과시키기 위해,
 * marked 의 기본 ALLOWED_TAGS 와 해외 법령 태그를 미리 합쳐둔다 (모듈 로드 시 1회).
 */
const HYBRID_ALLOWED_TAGS: ReadonlySet<string> = new Set<string>([...ALLOWED_TAGS, ...FOREIGN_LEGISLATION_HTML_TAGS]);

/**
 * EU 법령 본문은 통째로 XHTML 문서로 내려와서 marked 의 html 토큰에 그대로 들어온다.
 * 사용자에게 보일 필요가 없는 문서 선언·래퍼·메타 정보를 제거한 뒤 sanitize 에 통과시킨다.
 *
 * - XML 선언(<?xml ...?>) 및 DOCTYPE 선언 제거
 * - <head>...</head> 는 블록 단위로 통째로 제거 → 내부의 <style>/<script>/<meta http-equiv=refresh>
 *   같은 위험 케이스를 사전에 무력화.
 * - <html>/<body> 와 흩어진 <meta>/<link>/<title> 는 잔여 태그 단위로 제거.
 *
 * 속성값 안에 따옴표로 감싼 `>` 가 섞여 있어도 안전하게 매칭되도록 marked.ts 의 sanitizeHtml 과
 * 동일한 quoted-aware 속성 매칭 패턴을 사용한다.
 */
const STRIP_XML_DECL_RE = /<\?xml\b[^?]*\?>/gi;
const STRIP_DOCTYPE_RE = /<!DOCTYPE\b[^>]*>/gi;
const STRIP_HEAD_BLOCK_RE = /<head\b(?:[^>"']|"[^"]*"|'[^']*')*>[\s\S]*?<\/head>/gi;
const STRIP_DOCUMENT_TAGS_RE = /<\/?(?:html|body|meta|link|title)\b(?:[^>"']|"[^"]*"|'[^']*')*\/?>/gi;

const stripDocumentWrappers = (html: string): string =>
    html.replace(STRIP_XML_DECL_RE, '').replace(STRIP_DOCTYPE_RE, '').replace(STRIP_HEAD_BLOCK_RE, '').replace(STRIP_DOCUMENT_TAGS_RE, '');

/**
 * raw HTML 본문에 프로젝트 마크다운 스타일을 입히기 위한 태그-클래스 맵.
 *
 * raw HTML 원문은 pretty-printed 공백과 테이블 기반 지면 레이아웃이 섞여 들어오므로,
 * 일반 마크다운 renderer 보다 compact 한 Tailwind 클래스를 입혀 프리뷰 화면에서 과도한 여백을 줄인다.
 */
const HYBRID_TAG_CLASS_MAP: Record<string, string> = {
    p: 'text-gray-900 leading-[1.65]',
    h1: 'text-[1.75rem] font-bold mt-4 mb-0.5 text-gray-900 leading-[1.89]',
    h2: 'text-[1.5rem] font-bold mt-6 mb-0.5 text-gray-900 leading-[1.7]',
    h3: 'text-[1.25rem] font-semibold mt-4 mb-[0.675rem] text-gray-900 leading-[1.85]',
    h4: 'text-base font-semibold mt-4 mb-[0.675rem] text-gray-900 leading-[1.75]',
    h5: 'text-sm font-semibold mt-2.5 mb-0.5 text-gray-900 leading-[1.7]',
    h6: 'text-xs font-semibold mt-2.5 mb-0.5 text-gray-900 leading-[1.65]',
    table: 'w-full border-collapse bg-white mb-4 border-none',
    tr: 'border-b-2 border-gray-100',
    td: 'table-cell align-top px-2 py-1.5 leading-[1.5rem]',
    th: 'table-cell align-top bg-white px-2 py-1.5 border-none text-left leading-[1.5rem]',
    ul: 'list-disc mb-3.5 pl-5 leading-[1.89] text-gray-900',
    ol: 'list-decimal mb-3 pl-5 leading-[1.89] text-gray-900',
    li: 'mb-1 text-gray-900',
    a: 'text-indigo-500 underline font-semibold',
    blockquote: 'border-l-4 border-blue-100 pl-4 italic text-gray-600 mb-4',
    hr: 'border-t border-gray-100 my-4',
    strong: 'font-semibold',
    em: 'italic',
    img: 'max-w-full h-auto mt-4 mb-4',
};

/**
 * 여는 태그 매칭 패턴.
 *
 * - 1번 그룹: 태그명 (a-z 시작)
 * - 2번 그룹: 속성 문자열 (속성값 안의 `>` 는 따옴표로 무시되도록 quoted-aware)
 * - 3번 그룹: self-closing 여부 — 끝이 `/` 면 캡처
 *
 * marked.ts 의 sanitizeHtml 과 동일한 quoted-aware 패턴을 사용해 속성값 내 `>` 도 안전 처리한다.
 */
const STYLIZE_TAG_RE = /<([a-z][a-z0-9]*)\b((?:[^>"']|"[^"]*"|'[^']*')*)?(\/?)>/gi;
const STRIP_CLASS_ATTR_RE = /\s*class\s*=\s*(?:"[^"]*"|'[^']*')/gi;

/**
 * sanitize 한 raw HTML 의 각 여는 태그에 프로젝트 마크다운 클래스를 입혀 일반 마크다운 본문과
 * 시각 일관성을 맞춘다. 원본 class 속성(예: EU 법령 측 oj-normal)은 우리 클래스로 교체되어
 * 잔재가 남지 않는다.
 *
 * 보안 관점: 입력은 이미 sanitizeHtml 을 거친 결과이므로 위험 태그·속성·이벤트 핸들러는 모두 제거된
 * 상태이며, 본 함수는 그 위에 클래스만 부여한다.
 */
const stylizeForeignLegislationHtml = (html: string): string =>
    html.replace(STYLIZE_TAG_RE, (match, tagName: string, rawAttrs: string, selfClose: string) => {
        const lower = tagName.toLowerCase();
        const cls = HYBRID_TAG_CLASS_MAP[lower];
        if (!cls) return match;

        const cleanedAttrs = (rawAttrs || '').replace(STRIP_CLASS_ATTR_RE, '').trim();
        const attrPart = cleanedAttrs ? ` ${cleanedAttrs}` : '';
        const tail = selfClose ? ' />' : '>';
        return `<${lower}${attrPart} class="${cls}"${tail}`;
    });

export const hybridMarkdownConfig: MarkdownConfig = {
    htmlMode: 'sanitize',
    additionalAllowedTags: FOREIGN_LEGISLATION_HTML_TAGS,
    plugins: [
        {
            name: 'custom-del-renderer',
            renderer: {
                // del 은 인라인 토큰이므로 <p> 안에 다시 <p> 가 들어가면 invalid nesting 이 된다.
                // <span> + line-through 클래스로 인라인 구조를 유지한다.
                del: (text: string) => `<span class="text-gray-700 line-through">${text}</span>`,
            },
        },
        {
            name: 'hybrid-code-passthrough',
            renderer: {
                // 코드 블록·인라인 코드 모두 raw 마크업이 섞여 들어올 수 있으므로 sanitize 를 거친다.
                // 위험 태그는 escape, 화이트리스트(ALLOWED_TAGS + 해외 법령 태그)는 통과.
                // 코드 블록 안에는 <section> 같은 블록 레벨 요소가 섞일 수 있으므로 <p> 가 아닌 <div> 로 감싼다.
                code: (code: string) => `<div class="text-gray-900 leading-[1.65]">${sanitizeHtml(code, HYBRID_ALLOWED_TAGS)}</div>`,
                codespan: (code: string) => sanitizeHtml(code, HYBRID_ALLOWED_TAGS),
            },
        },
        {
            name: 'foreign-legislation-html-passthrough',
            renderer: {
                // EU 법령처럼 통째로 XHTML 문서가 html 토큰으로 내려올 수 있다.
                // 1) 문서 래퍼 제거 → 2) sanitize → 3) 프로젝트 마크다운 클래스 부여 순으로 처리한다.
                html: (html: string) => stylizeForeignLegislationHtml(sanitizeHtml(stripDocumentWrappers(html), HYBRID_ALLOWED_TAGS)),
            },
        },
    ],
};
